import { useState } from 'react';
import { Cell, Color, COLOR_HEX, RingSize } from '@otrio/engine';

interface BoardProps {
  board: Cell[];
  selectedSize: RingSize | null;
  currentColor: Color;
  winner?: Color | null;
  winLine: number[] | null;
  onDrop: (cellIndex: number) => void;
  onCellClick: (cellIndex: number) => void;
}

const CELL = 130;
const PAD = 15;
const SVG_SIZE = CELL * 3 + PAD * 2;

const RING: Record<RingSize, { r: number; sw: number }> = {
  large:  { r: 52, sw: 13 },
  medium: { r: 34, sw: 13 },
  small:  { r: 16, sw: 13 },
};

function cellCenter(index: number) {
  return {
    cx: PAD + (index % 3) * CELL + CELL / 2,
    cy: PAD + Math.floor(index / 3) * CELL + CELL / 2,
  };
}

export function Board({ board, selectedSize, currentColor, winner, winLine, onDrop, onCellClick }: BoardProps) {
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  function handleDragOver(e: React.DragEvent<SVGRectElement>, index: number) {
    e.preventDefault();
    setDragOverIndex(index);
  }

  function handleDrop(e: React.DragEvent<SVGRectElement>, index: number) {
    e.preventDefault();
    setDragOverIndex(null);
    onDrop(index);
  }

  return (
    <svg
      width={SVG_SIZE}
      height={SVG_SIZE}
      viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
      style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
    >
      {/* Board background */}
      <rect x={0} y={0} width={SVG_SIZE} height={SVG_SIZE} fill="#1e2a3a" rx={16} />

      {/* Grid lines */}
      {[1, 2].map(i => (
        <g key={i}>
          <line
            x1={PAD + i * CELL} y1={PAD + 8}
            x2={PAD + i * CELL} y2={SVG_SIZE - PAD - 8}
            stroke="#2d3f52" strokeWidth={2}
          />
          <line
            x1={PAD + 8}        y1={PAD + i * CELL}
            x2={SVG_SIZE - PAD - 8} y2={PAD + i * CELL}
            stroke="#2d3f52" strokeWidth={2}
          />
        </g>
      ))}

      {/* Cells */}
      {board.map((cell, index) => {
        const { cx, cy } = cellCenter(index);
        const isWinCell = winLine?.includes(index) ?? false;
        const isDragTarget = dragOverIndex === index;
        const isValidDrop = selectedSize !== null && cell[selectedSize] === null;
        const canInteract = selectedSize !== null && cell[selectedSize] === null;

        return (
          <g key={index}>
            {/* Clickable / droppable background rect */}
            <rect
              x={cx - CELL / 2 + 6} y={cy - CELL / 2 + 6}
              width={CELL - 12} height={CELL - 12}
              rx={10}
              fill={
                isWinCell
                  ? 'rgba(255,215,0,0.18)'
                  : isDragTarget && isValidDrop
                    ? 'rgba(39,174,96,0.18)'
                    : 'transparent'
              }
              stroke={
                isWinCell
                  ? 'rgba(255,215,0,0.7)'
                  : isDragTarget && isValidDrop
                    ? '#27ae60'
                    : 'transparent'
              }
              strokeWidth={2}
              style={{ cursor: canInteract ? 'pointer' : 'default' }}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragLeave={() => setDragOverIndex(null)}
              onClick={() => onCellClick(index)}
            />

            {/* Rings: large → medium → small (outer first so inner renders on top) */}
            {(['large', 'medium', 'small'] as RingSize[]).map((size, si) => {
              const { r, sw } = RING[size];
              const color = cell[size];
              const isWinRing = isWinCell && color !== null && color === winner;
              return (
                <circle
                  key={size}
                  cx={cx} cy={cy} r={r}
                  fill="none"
                  stroke={color ? COLOR_HEX[color] : '#3a4f66'}
                  strokeWidth={sw}
                  opacity={color ? 1 : 0.45}
                  className={isWinRing ? 'win-ring' : undefined}
                  style={{
                    pointerEvents: 'none',
                    ...(isWinRing
                      ? { '--win-glow': COLOR_HEX[color!], animationDelay: `${si * 0.18}s` } as React.CSSProperties
                      : {}),
                  }}
                />
              );
            })}
            {/* Ghost ring preview on drag hover */}
            {isDragTarget && isValidDrop && selectedSize && (
              <circle
                cx={cx} cy={cy}
                r={RING[selectedSize].r}
                fill="none"
                stroke={COLOR_HEX[currentColor]}
                strokeWidth={RING[selectedSize].sw}
                opacity={0.55}
                style={{ pointerEvents: 'none' }}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}
